import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import BottomNav from "@/components/BottomNav";
import TopNav from "@/components/TopNav";
import InstallPrompt from "@/components/InstallPrompt";
import OfflineIndicator from "@/components/OfflineIndicator";
import KeyboardShortcutsOverlay from "@/components/KeyboardShortcuts";
import SkipToContent from "@/components/SkipToContent";
import RouteSeo from "@/components/RouteSeo";
import Index from "./pages/Index";
import PipeSizing from "./pages/PipeSizing";
import Electrical from "./pages/Electrical";
import Ductwork from "./pages/Ductwork";
import { Navigate } from "react-router-dom";
import JobLog from "./pages/JobLog";
import Install from "./pages/Install";
import Settings from "./pages/Settings";
import Structural from "./pages/Structural";
import Support from "./pages/Support";
import Help from "./pages/Help";
import PipeSketch from "./pages/PipeSketch";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" as const } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15, ease: "easeIn" as const } },
};

const GlobalSearchShortcut = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        if (location.pathname !== "/") {
          navigate("/?focus=search");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, location.pathname]);

  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen"
      >
        <Routes location={location}>
          <Route path="/" element={<Index />} />
          <Route path="/pipe" element={<PipeSizing />} />
          <Route path="/electrical" element={<Electrical />} />
          <Route path="/ductwork" element={<Ductwork />} />
          <Route path="/calculators" element={<Navigate to="/" replace />} />
          <Route path="/structural" element={<Structural />} />
          <Route path="/converter" element={<Navigate to="/" replace />} />
          <Route path="/joblog" element={<JobLog />} />
          <Route path="/formulas" element={<Navigate to="/" replace />} />
          <Route path="/install" element={<Install />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/support" element={<Support />} />
          <Route path="/help" element={<Help />} />
          <Route path="/sketch" element={<PipeSketch />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
        <BrowserRouter>
          <RouteSeo />
          <GlobalSearchShortcut />
          <KeyboardShortcutsOverlay />
        <div className="min-h-screen bg-background">
          <SkipToContent />
          <OfflineIndicator />
          <TopNav />
           <main id="main-content" className="md:pt-12" role="main">
            <AnimatedRoutes />
          </main>
          <BottomNav />
          <InstallPrompt />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
